// Shared caption helpers — used by the admin editor (AdminPanel.js) and the client
// player (ClientApp.js). Captions are stored as ONE array of cues, each carrying both
// languages aligned to the same timestamp:
//   { id, start, end, sw, en }   (start/end in seconds)
// From that single source we render the live overlay, and generate .vtt / .srt
// subtitle files and the clean prose used for the Word/book export.

// ── Timestamp formatting ──────────────────────────────────────────────────────
// mm:ss for the compact editor fields and the player.
export function mmss(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Parse "m:ss" / "mm:ss" / "h:mm:ss" (or a plain number of seconds) back to seconds.
// Returns null when it can't be parsed, so callers can ignore a bad edit.
export function parseMmss(str) {
  if (str == null) return null;
  const t = String(str).trim();
  if (t === '') return null;
  if (/^\d+(\.\d+)?$/.test(t)) return Number(t); // bare seconds
  const parts = t.split(':').map(p => p.trim());
  if (parts.some(p => p === '' || isNaN(Number(p)))) return null;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
}

// HH:MM:SS.mmm (WebVTT) / HH:MM:SS,mmm (SRT differ only by the decimal separator).
function stamp(sec, sep) {
  const total = Math.max(0, sec || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  const ms = Math.round((total - Math.floor(total)) * 1000);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}${sep}${pad(ms, 3)}`;
}

// ── Subtitle file generation ──────────────────────────────────────────────────
// lang = 'sw' | 'en'. Cues missing that language's text are skipped.
export function cuesToVtt(cues, lang) {
  const lines = ['WEBVTT', ''];
  (cues || []).forEach((c, i) => {
    const text = (c[lang] || '').trim();
    if (!text) return;
    lines.push(String(i + 1));
    lines.push(`${stamp(c.start, '.')} --> ${stamp(c.end, '.')}`);
    lines.push(text);
    lines.push('');
  });
  return lines.join('\n');
}

export function cuesToSrt(cues, lang) {
  const blocks = [];
  let n = 0;
  (cues || []).forEach(c => {
    const text = (c[lang] || '').trim();
    if (!text) return;
    n += 1;
    blocks.push(`${n}\n${stamp(c.start, ',')} --> ${stamp(c.end, ',')}\n${text}\n`);
  });
  return blocks.join('\n');
}

// ── Active-cue lookup ─────────────────────────────────────────────────────────
// Returns the index of the cue covering time t (start ≤ t < end), else -1.
// Linear scan is fine — a session has a few hundred cues at most and this only
// runs on timeupdate (~4×/s).
export function activeCueIndex(cues, t) {
  if (!cues || !cues.length) return -1;
  for (let i = 0; i < cues.length; i++) {
    if (t >= cues[i].start && t < cues[i].end) return i;
  }
  return -1;
}

// ── Prose fallback ────────────────────────────────────────────────────────────
// The pipeline stores a clean paragraph version (proseSw/proseEn) for the book
// export. If it's ever missing, fall back to stitching the cue text together so an
// export still produces something readable.
export function proseFor(caption, lang) {
  const key = lang === 'en' ? 'proseEn' : 'proseSw';
  if (caption && caption[key] && caption[key].trim()) return caption[key].trim();
  return (caption?.cues || [])
    .map(c => (c[lang] || '').trim())
    .filter(Boolean)
    .join(' ');
}

// ── Filenames & downloads ─────────────────────────────────────────────────────
export function sanitizeFilename(str) {
  return String(str || 'file')
    .replace(/[\\/:*?"<>|]+/g, ' ') // strip path-unsafe chars
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'file';
}

// Trigger a client-side download of text content as a Blob (larger/binary-safe
// version of the data: URI idiom already used for the clients CSV export).
export function downloadText(filename, text, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const LANG_LABEL = { orig: 'Original', sw: 'Swahili', en: 'English' };
